import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GitBranch,
  ArrowRight,
  X,
  Lock,
  AlertTriangle,
  CornerDownRight,
  CheckCircle2,
} from 'lucide-react';
import {
  scheduleAfterMove,
  scheduleShiftConnectedComponent,
  isTaskLocked,
  type ScheduleResult,
  type TaskDateState,
} from '../../utils/taskSchedule';
import type { ProjectTask } from '../../types';
import { shouldDismissModalOverlay } from '../../utils/modalGhostClick';

export type DependencyCommitMode = 'direct' | 'ripple' | 'shiftConnected';

interface DependencyDateCommitModalProps {
  /** The task being moved (current canonical state from `projectSchedulingNodes`). */
  task: ProjectTask;
  /** Requested new dates (may differ from `task.startDate` / `task.endDate`). */
  newStart: Date;
  newEnd: Date;
  /** All scheduling nodes for the project (tasks + onboarding bridges + milestones). */
  schedulingNodes: ProjectTask[];
  /** Tells caller which mode to apply, plus whether to remember it for this project. */
  onApply: (mode: DependencyCommitMode, remember: boolean) => void;
  onCancel: () => void;
}

interface PreviewRow {
  id: string;
  title: string;
  oldStart: Date;
  oldEnd: Date;
  newStart: Date;
  newEnd: Date;
  locked: boolean;
}

/** Display subset of scheduler lock violations (start and/or finish constraints after FF/SF). */
interface LockViolationRow {
  id: string;
  title: string;
  pinnedReason: 'completed' | 'explicit';
  requiredStart?: Date;
  currentStart?: Date;
  requiredEnd?: Date;
  currentEnd?: Date;
}

interface ModePreview {
  rows: PreviewRow[];
  violations: LockViolationRow[];
  unchangedCount: number;
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatLockViolationSentence(v: LockViolationRow): string {
  const explain =
    v.pinnedReason === 'completed'
      ? 'Completed tasks keep their dates unless you revise completion or actual dates.'
      : 'Schedule-locked bars stay put until you unlock them.';
  const hasStart = v.requiredStart != null && v.currentStart != null;
  const hasEnd = v.requiredEnd != null && v.currentEnd != null;
  const startPart = hasStart
    ? `would need to start ${fmtDate(v.requiredStart!)} but stays at ${fmtDate(v.currentStart!)}`
    : '';
  const endPart = hasEnd
    ? `would need to finish by ${fmtDate(v.requiredEnd!)} but stays ending ${fmtDate(v.currentEnd!)}`
    : '';
  let body = '';
  if (hasStart && hasEnd) body = `${startPart}; ${endPart}.`;
  else if (hasStart) body = `${startPart}.`;
  else if (hasEnd) body = `${endPart}.`;
  else body = 'Cannot satisfy link constraints while pinned.';
  return `${body} ${explain}`;
}

/** Warning chip beside mode cards — completed vs explicitly locked successors. */
function lockViolationChipLabel(vs: LockViolationRow[]): string {
  const n = vs.length;
  const completed = vs.filter((v) => v.pinnedReason === 'completed').length;
  const explicit = n - completed;
  const bits: string[] = [];
  if (completed > 0) {
    bits.push(`${completed} completed`);
  }
  if (explicit > 0) {
    bits.push(`${explicit} locked`);
  }
  const qualifier = n === 1 ? 'successor pins the strand' : 'successors pin the strand';
  return `${bits.join(', ')} — ${qualifier}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildPreview(
  schedulingNodes: ProjectTask[],
  result: ScheduleResult,
  movedId: string,
  fallbackMoved: { start: Date; end: Date },
): ModePreview {
  const titleById = new Map<string, string>();
  const origDates = new Map<string, { startDate: Date; endDate: Date; locked: boolean }>();
  for (const n of schedulingNodes) {
    if (!n.id) continue;
    titleById.set(n.id, n.title || 'Untitled');
    origDates.set(n.id, {
      startDate: new Date(n.startDate),
      endDate: new Date(n.endDate),
      locked: isTaskLocked(n),
    });
  }

  const rows: PreviewRow[] = [];
  const seen = new Set<string>();

  result.dates.forEach((dates: TaskDateState, id: string) => {
    seen.add(id);
    const orig = origDates.get(id);
    if (!orig) return;
    const movedThis = id === movedId;
    if (!movedThis && sameDay(orig.startDate, dates.startDate) && sameDay(orig.endDate, dates.endDate)) {
      return;
    }
    rows.push({
      id,
      title: titleById.get(id) || 'Untitled',
      oldStart: orig.startDate,
      oldEnd: orig.endDate,
      newStart: dates.startDate,
      newEnd: dates.endDate,
      locked: orig.locked,
    });
  });

  // Move-only mode produces no engine result; synthesize a row for the moved task itself.
  if (rows.length === 0 && fallbackMoved && titleById.has(movedId)) {
    const orig = origDates.get(movedId)!;
    rows.push({
      id: movedId,
      title: titleById.get(movedId) || 'Untitled',
      oldStart: orig.startDate,
      oldEnd: orig.endDate,
      newStart: fallbackMoved.start,
      newEnd: fallbackMoved.end,
      locked: orig.locked,
    });
  }

  rows.sort((a, b) => {
    if (a.id === movedId) return -1;
    if (b.id === movedId) return 1;
    return a.newStart.getTime() - b.newStart.getTime();
  });

  const violations: LockViolationRow[] = (result.violations ?? []).map((v) => {
    const node = schedulingNodes.find((n) => n.id === v.id);
    const pinnedReason: 'completed' | 'explicit' =
      v.pinnedReason ?? (node?.status === 'completed' ? 'completed' : 'explicit');
    return {
      id: v.id,
      title: titleById.get(v.id) || 'Untitled',
      pinnedReason,
      requiredStart: v.requiredStart,
      currentStart: v.currentStart,
      requiredEnd: v.requiredEnd,
      currentEnd: v.currentEnd,
    };
  });

  // Unchanged successors that the engine considered but did not need to move.
  let unchangedCount = 0;
  result.dates.forEach((dates, id) => {
    if (id === movedId) return;
    if (seen.has(id)) {
      const exists = rows.find((r) => r.id === id);
      if (exists) return;
    }
    const orig = origDates.get(id);
    if (!orig) return;
    if (sameDay(orig.startDate, dates.startDate) && sameDay(orig.endDate, dates.endDate)) {
      unchangedCount += 1;
    }
  });

  return { rows, violations, unchangedCount };
}

const ModeCard: React.FC<{
  modeId: DependencyCommitMode;
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  recommended?: boolean;
  description: string;
  preview: ModePreview;
  movedTaskId: string;
  onSelect: () => void;
  hotkey: string;
}> = ({ modeId, selected, icon, label, recommended, description, preview, movedTaskId, onSelect, hotkey }) => {
  const totalAffected = preview.rows.length;
  const othersAffected = preview.rows.filter((r) => r.id !== movedTaskId).length;
  const onlyEditedBarMoves = totalAffected > 0 && othersAffected === 0;
  const lockedSkippedRows = preview.rows.filter((r) => r.id !== movedTaskId && r.locked);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-mode={modeId}
      className={`pm-dep-date-card ${selected ? 'pm-dep-date-card--selected' : ''}`}
      onClick={onSelect}
    >
      <div className="pm-dep-date-card-head">
        <span className="pm-dep-date-card-icon" aria-hidden>
          {icon}
        </span>
        <div className="pm-dep-date-card-title-row">
          <span className="pm-dep-date-card-title">{label}</span>
          {recommended && <span className="pm-dep-date-card-badge">Recommended</span>}
        </div>
        <span className="pm-dep-date-card-hotkey" aria-hidden>
          {hotkey}
        </span>
      </div>
      <p className="pm-dep-date-card-desc">{description}</p>
      {modeId !== 'direct' && onlyEditedBarMoves && (
        <p className="pm-dep-date-card-scope-note">
          For this move,{' '}
          <strong>only this bar&apos;s dates change</strong> — nothing else shifts; links stay intact.
        </p>
      )}

      <div className="pm-dep-date-card-summary">
        {totalAffected > 0 ? (
          onlyEditedBarMoves ? (
            <span className="pm-dep-date-card-count pm-dep-date-card-count--muted">
              Only this bar updates
            </span>
          ) : (
            <span className="pm-dep-date-card-count">
              <CornerDownRight size={11} aria-hidden />
              {othersAffected === 1
                ? 'This bar plus 1 other task'
                : `This bar plus ${othersAffected} other tasks`}
            </span>
          )
        ) : (
          <span className="pm-dep-date-card-count pm-dep-date-card-count--muted">
            <CheckCircle2 size={11} aria-hidden /> No other tasks shift
          </span>
        )}
        {preview.violations.length > 0 && (
          <span className="pm-dep-date-card-count pm-dep-date-card-count--warn">
            <AlertTriangle size={11} aria-hidden /> {lockViolationChipLabel(preview.violations)}
          </span>
        )}
      </div>

      {totalAffected > 0 && (
        <ul className="pm-dep-date-card-list">
          {preview.rows.slice(0, 5).map((r) => (
            <li key={r.id} className={r.locked && r.id !== movedTaskId ? 'pm-dep-date-card-row-locked' : ''}>
              <span className="pm-dep-date-card-row-name">
                {r.locked && r.id !== movedTaskId ? <Lock size={10} aria-hidden /> : null}
                {r.title}
              </span>
              <span className="pm-dep-date-card-row-dates">
                {fmtDate(r.oldStart)}&nbsp;→&nbsp;<strong>{fmtDate(r.newStart)}</strong>
              </span>
            </li>
          ))}
          {preview.rows.length > 5 && (
            <li className="pm-dep-date-card-more">+ {preview.rows.length - 5} more</li>
          )}
        </ul>
      )}

      {preview.violations.length > 0 && (
        <ul className="pm-dep-date-card-warnings">
          {preview.violations.slice(0, 3).map((v) => (
            <li
              key={v.id}
              className={
                v.pinnedReason === 'completed'
                  ? 'pm-dep-date-card-warning--completed'
                  : 'pm-dep-date-card-warning--locked'
              }
            >
              {v.pinnedReason === 'completed' ? (
                <CheckCircle2 size={11} aria-hidden />
              ) : (
                <Lock size={11} aria-hidden />
              )}
              <span>
                <strong>{v.title}</strong> {formatLockViolationSentence(v)}
              </span>
            </li>
          ))}
          {preview.violations.length > 3 && (
            <li className="pm-dep-date-card-more">+ {preview.violations.length - 3} more conflicts</li>
          )}
        </ul>
      )}

      {lockedSkippedRows.length > 0 && (
        <p className="pm-dep-date-card-locked-note">
          {lockedSkippedRows.length} locked task{lockedSkippedRows.length === 1 ? '' : 's'} will stay
          pinned.
        </p>
      )}
    </button>
  );
};

const DependencyDateCommitModal: React.FC<DependencyDateCommitModalProps> = ({
  task,
  newStart,
  newEnd,
  schedulingNodes,
  onApply,
  onCancel,
}) => {
  const movedId = task.id!;

  const ripplePreview = useMemo<ModePreview>(() => {
    const r = scheduleAfterMove(schedulingNodes, movedId, newStart, newEnd);
    return buildPreview(schedulingNodes, r, movedId, { start: newStart, end: newEnd });
  }, [schedulingNodes, movedId, newStart, newEnd]);

  const shiftPreview = useMemo<ModePreview>(() => {
    const r = scheduleShiftConnectedComponent(schedulingNodes, movedId, newStart, newEnd);
    return buildPreview(schedulingNodes, r, movedId, { start: newStart, end: newEnd });
  }, [schedulingNodes, movedId, newStart, newEnd]);

  const directPreview = useMemo<ModePreview>(() => {
    const orig = schedulingNodes.find((n) => n.id === movedId);
    if (!orig) {
      return { rows: [], violations: [], unchangedCount: 0 };
    }
    const rows: PreviewRow[] = [
      {
        id: movedId,
        title: orig.title || 'Untitled',
        oldStart: new Date(orig.startDate),
        oldEnd: new Date(orig.endDate),
        newStart,
        newEnd,
        locked: false,
      },
    ];
    return { rows, violations: [], unchangedCount: 0 };
  }, [schedulingNodes, movedId, newStart, newEnd]);

  const [mode, setMode] = useState<DependencyCommitMode>('ripple');
  const [remember, setRemember] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  /** Set on mount — drag mouseup opens this modal, then a trailing click would otherwise hit the overlay and cancel. */
  const openedAtRef = useRef(Date.now());
  /** Ghost clicks only fire `click` on the overlay; real dismiss presses down on the backdrop first. */
  const overlayMouseDownArmedRef = useRef(false);

  useEffect(() => {
    openedAtRef.current = Date.now();
    overlayMouseDownArmedRef.current = false;
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === '1') setMode('direct');
      if (e.key === '2') setMode('ripple');
      if (e.key === '3') setMode('shiftConnected');
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onApply(mode, remember);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, remember, onApply, onCancel]);

  const handleOverlayMouseDown = (e: React.MouseEvent) => {
    overlayMouseDownArmedRef.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = () => {
    const armed = overlayMouseDownArmedRef.current;
    overlayMouseDownArmedRef.current = false;
    if (
      !shouldDismissModalOverlay({
        openedAtMs: openedAtRef.current,
        overlayMouseDownArmed: armed,
      })
    ) {
      return;
    }
    onCancel();
  };

  return (
    <div
      className="pm-dep-date-modal-overlay"
      role="presentation"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
      ref={overlayRef}
    >
      <div
        className="pm-dep-date-modal pm-dep-date-modal--v2"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-dep-date-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pm-dep-date-modal-header">
          <div>
            <h3 id="pm-dep-date-modal-title">How should &ldquo;{task.title}&rdquo; move?</h3>
            <p className="pm-dep-date-modal-task">
              New dates: <strong>{fmtDate(newStart)}</strong> &ndash;{' '}
              <strong>{fmtDate(newEnd)}</strong>. Choose whether any <strong>linked</strong> bars need to slide with
              this one.
            </p>
          </div>
          <button
            type="button"
            className="pm-dep-date-modal-x"
            aria-label="Close"
            onClick={onCancel}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="pm-dep-date-modal-cards" role="radiogroup" aria-label="Move mode">
          <ModeCard
            modeId="direct"
            selected={mode === 'direct'}
            icon={<ArrowRight size={16} />}
            label="Move only this task"
            description="Updates just this bar. Dependencies remain; successors stay on their dates and lag is inferred from where you dropped this bar."
            preview={directPreview}
            movedTaskId={movedId}
            onSelect={() => setMode('direct')}
            hotkey="1"
          />
          <ModeCard
            modeId="ripple"
            selected={mode === 'ripple'}
            icon={<CornerDownRight size={16} />}
            label="Cascade to dependents"
            recommended
            description="Lets the schedule push or pull successors when your new dates would break a dependency. Locked successors stay pinned; others move only if constraints require it."
            preview={ripplePreview}
            movedTaskId={movedId}
            onSelect={() => setMode('ripple')}
            hotkey="2"
          />
          <ModeCard
            modeId="shiftConnected"
            selected={mode === 'shiftConnected'}
            icon={<GitBranch size={16} />}
            label="Shift entire chain"
            description="Slides every task linked in the same dependency group by the same calendar offset (useful when the whole strand should drift together). Locked tasks anchor the strand."
            preview={shiftPreview}
            movedTaskId={movedId}
            onSelect={() => setMode('shiftConnected')}
            hotkey="3"
          />
        </div>

        <footer className="pm-dep-date-modal-footer">
          <label className="pm-dep-date-modal-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>Don&rsquo;t ask again for this project</span>
          </label>
          <div className="pm-dep-date-modal-footer-actions">
            <button
              type="button"
              className="pm-dep-date-modal-btn pm-dep-date-modal-btn--ghost"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pm-dep-date-modal-btn pm-dep-date-modal-btn--primary"
              onClick={() => onApply(mode, remember)}
            >
              Apply
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DependencyDateCommitModal;
